/**
 * The fill registry's provider (plan-019 WAPP-HOST; ED-25, ED-26; design §31.9.1, §31.9.5).
 *
 * Mounted once, by the portal's root route component (`../help-root.tsx`), above every page — signed in and signed out
 * — so a reviewed form anywhere registers through `useHelpFillable` without the shell knowing. It hands the forms the
 * registry and the router `href` they are drawn on (read here, once, so a form outside the provider — every existing
 * form test — never needs a router for the hook: outside it the hook does nothing).
 *
 * `useHelpFillSuspension(reason, active)` lets a screen suspend fills while it holds a state the person must finish
 * first (a step-up confirmation it opened); the registry also refuses while any modal dialog stands over the page.
 */
import { useLocation } from "@tanstack/react-router";
import { createContext, use, useEffect, useMemo, type ReactNode } from "react";

import type { HelpFillRegistry, HelpFillSuspension } from "./fill-registry.js";
import "./help-fill.css";

export interface HelpFillContextValue {
  readonly registry: HelpFillRegistry;
  /** The router `href` the page is drawn on, recorded with each registration. */
  readonly href: string;
}

export const HelpFillContext = createContext<HelpFillContextValue | null>(null);

export function HelpFillProvider({ registry, children }: { readonly registry: HelpFillRegistry; readonly children: ReactNode }): ReactNode {
  const href = useLocation({ select: (location) => location.href });
  const value = useMemo<HelpFillContextValue>(() => ({ registry, href }), [registry, href]);
  return <HelpFillContext value={value}>{children}</HelpFillContext>;
}

/** Suspends fills for `reason` while `active`; does nothing outside the provider. */
export function useHelpFillSuspension(reason: HelpFillSuspension, active: boolean): void {
  const context = use(HelpFillContext);
  const registry = context === null ? null : context.registry;
  useEffect(() => {
    if (registry === null || !active) return undefined;
    return registry.suspend(reason);
  }, [registry, reason, active]);
}
